/**
 * Menu kontekstowe nagłówka kolumny — Krok 7.2.1 (parytet DevExpress/Excel).
 *
 * Otwiera się prawym przyciskiem myszy na `<th>` w ConfigurableTable.
 * 11 opcji w 4 grupach:
 *   1. Sortowanie (ASC / DESC)
 *   2. Grupowanie (Group By This Column / Show Group By Box) — disabled, TODO
 *   3. Layout (Split / Hide / Column Chooser / Best Fit × 2) — częściowo disabled
 *   4. Filtry + UI (Filter Editor / Show Find Panel / Hide Auto Filter Row /
 *      Conditional Formatting / Rename)
 *
 * Conditional Formatting otwiera submenu (Krok 7.2.2 — tu placeholder).
 *
 * Pozycjonowanie: absolute przy (x, y) z eventu prawo-kliku, z fallbackiem
 * (gdy menu wyjeżdża poza viewport — flip do drugiej strony). Esc / klik
 * poza menu → onClose.
 */

import { useEffect, useRef, useState } from 'react';
import type { FormattingRuleType, HighlightCellsCondition } from '@saas/shared';
import {
  ArrowDown,
  ArrowDownAZ,
  ArrowUp,
  ArrowUpAZ,
  BarChart3,
  ChevronRight,
  Copy,
  Equal,
  Eye,
  EyeOff,
  Filter,
  Layers,
  LayoutGrid,
  MoveHorizontal,
  Palette,
  Search,
  Settings as SettingsIcon,
  Sparkles,
  SplitSquareHorizontal,
  Star,
  Type,
} from 'lucide-react';

export type ColumnContextAction =
  | 'sort_asc'
  | 'sort_desc'
  | 'group_by'
  | 'show_group_by_box'
  | 'split'
  | 'hide'
  | 'column_chooser'
  | 'best_fit'
  | 'best_fit_all'
  | 'filter_editor'
  | 'show_find_panel'
  | 'toggle_auto_filter_row'
  | 'conditional_formatting'
  | 'rename';

/**
 * Pre-set reguły wybrany w submenu Conditional Formatting. Parent (Configurable-
 * Table) używa do otwarcia FormattingRuleEditorModal z pre-wypełnionymi polami.
 */
export interface CfRulePreset {
  type: FormattingRuleType;
  condition?: Partial<HighlightCellsCondition> & Record<string, unknown>;
}

interface ColumnContextMenuProps {
  open: boolean;
  /** Współrzędne ekranu z eventu prawo-kliku. */
  anchor: { x: number; y: number } | null;
  /** Czy filter row pod headerami jest widoczny (label „Show/Hide Auto Filter Row"). */
  isFilterRowVisible: boolean;
  onClose: () => void;
  onAction: (action: ColumnContextAction) => void;
  /**
   * Krok 7.2.2 — wybór pre-set'a reguły z submenu Conditional Formatting.
   * Parent (ConfigurableTable) używa do otwarcia FormattingRuleEditorModal.
   */
  onCfPresetSelected?: (preset: CfRulePreset) => void;
  /** Krok 7.2.4 — otwarcie managera reguł z opcji „Manage Rules". */
  onCfManageRules?: () => void;
}

interface MenuItem {
  action: ColumnContextAction;
  label: string;
  Icon: typeof Filter;
  /** TRUE = wpisany tooltip „W przygotowaniu", klik nic nie robi. */
  disabled?: boolean;
  /** Submenu — pokazuje chevron, klik wywołuje action (parent rozwija submenu). */
  hasSubmenu?: boolean;
}

interface MenuGroup {
  items: MenuItem[];
}

export function ColumnContextMenu({
  open,
  anchor,
  isFilterRowVisible,
  onClose,
  onAction,
  onCfPresetSelected,
  onCfManageRules,
}: ColumnContextMenuProps): React.ReactElement | null {
  const menuRef = useRef<HTMLDivElement>(null);
  /**
   * Czy submenu „Formatowanie warunkowe" jest otwarte (`'cf'`) czy nie (`null`).
   * Drążenie w głąb (highlight/unique/color_scale/icon_set) obsługuje już sam
   * `CfSubmenu` przez flyouty obok — parent trzyma tylko flagę otwarcia rootu.
   */
  const [submenuPath, setSubmenuPath] = useState<'cf' | null>(null);

  // Esc + klik poza menu zamykają. Esc w fazie przechwytywania + `preventDefault`: okno nakładkowe
  // (`Overlay`), w którym stoi tabela, widzi wtedy Esc jako już obsłużony i nie zamyka się razem
  // z menu.
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      onClose();
    };
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('keydown', handleKey, true);
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('keydown', handleKey, true);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [open, onClose]);

  // Reset submenu przy każdym otwarciu menu.
  useEffect(() => {
    if (!open) setSubmenuPath(null);
  }, [open]);

  if (!open || !anchor) return null;

  // Grupy menu — separator między grupami.
  const groups: MenuGroup[] = [
    {
      items: [
        { action: 'sort_asc', label: 'Sortuj rosnąco', Icon: ArrowUpAZ },
        { action: 'sort_desc', label: 'Sortuj malejąco', Icon: ArrowDownAZ },
      ],
    },
    {
      items: [
        { action: 'group_by', label: 'Grupuj po tej kolumnie', Icon: Layers, disabled: true },
        {
          action: 'show_group_by_box',
          label: 'Pokaż pasek grupowania',
          Icon: LayoutGrid,
          disabled: true,
        },
        { action: 'split', label: 'Podziel', Icon: SplitSquareHorizontal, disabled: true },
      ],
    },
    {
      items: [
        { action: 'hide', label: 'Ukryj kolumnę', Icon: EyeOff },
        { action: 'column_chooser', label: 'Wybór kolumn…', Icon: SettingsIcon },
        { action: 'best_fit', label: 'Dopasuj szerokość', Icon: MoveHorizontal, disabled: true },
        {
          action: 'best_fit_all',
          label: 'Dopasuj wszystkie kolumny',
          Icon: MoveHorizontal,
          disabled: true,
        },
      ],
    },
    {
      items: [
        { action: 'filter_editor', label: 'Edytor filtra…', Icon: Filter },
        { action: 'show_find_panel', label: 'Pokaż wyszukiwarkę', Icon: Search, disabled: true },
        {
          action: 'toggle_auto_filter_row',
          label: isFilterRowVisible ? 'Ukryj wiersz filtrów' : 'Pokaż wiersz filtrów',
          Icon: Eye,
        },
        {
          action: 'conditional_formatting',
          label: 'Formatowanie warunkowe',
          Icon: Sparkles,
          hasSubmenu: true,
        },
        { action: 'rename', label: 'Zmień nazwę', Icon: Type, disabled: true },
      ],
    },
  ];

  // Pozycjonowanie — flip do prawej/dołu gdy wyjeżdża poza viewport.
  // Zakładamy menu max 260×400. Lepiej byłoby zmierzyć, ale efekt akceptowalny.
  const MENU_W = 260;
  const MENU_H = 400;
  const left = anchor.x + MENU_W > window.innerWidth ? anchor.x - MENU_W : anchor.x;
  const top = anchor.y + MENU_H > window.innerHeight ? anchor.y - MENU_H : anchor.y;

  return (
    <div
      ref={menuRef}
      role="menu"
      data-testid="column-context-menu"
      className="fixed z-[10000] min-w-[260px] py-1 bg-white border border-gray-200 rounded-lg shadow-lg dark:bg-gray-800 dark:border-gray-700"
      style={{ left, top }}
    >
      {groups.map((group, gi) => (
        <div key={gi}>
          {gi > 0 && <div className="my-1 border-t border-gray-200 dark:border-gray-700" />}
          {group.items.map((item) => (
            <MenuRow
              key={item.action}
              item={item}
              isActive={item.action === 'conditional_formatting' && submenuPath === 'cf'}
              onClick={() => {
                if (item.disabled) return;
                if (item.action === 'conditional_formatting') {
                  setSubmenuPath((p) => (p === 'cf' ? null : 'cf'));
                  return;
                }
                onAction(item.action);
                if (!item.hasSubmenu) onClose();
              }}
            />
          ))}
        </div>
      ))}

      {/* Submenu Conditional Formatting — Krok 7.2.2.
          Otwiera się obok parent menu po prawej stronie. Rozwijalne kategorie
          (podświetlanie / unikalne / skala kolorów / ikony) otwierają własny
          flyout OBOK (Excel-style) — root zostaje widoczny, bez podmiany. */}
      {submenuPath === 'cf' && (
        <CfSubmenu
          onPreset={(preset) => {
            onCfPresetSelected?.(preset);
            onClose();
          }}
          onManageRules={() => {
            onCfManageRules?.();
            onClose();
          }}
        />
      )}
    </div>
  );
}

// ── Submenu Conditional Formatting (Krok 7.2.2) ──────────────────────────────

interface CfSubmenuProps {
  onPreset: (preset: CfRulePreset) => void;
  onManageRules: () => void;
}

/**
 * Submenu „Formatowanie warunkowe" — flyout Excel-style (Krok 7.2.2, UX fix
 * 2026-06-01). Root (6 pozycji) zostaje stale widoczny; rozwijalne kategorie
 * (podświetlanie / unikalne / skala kolorów / ikony) otwierają własny panel
 * OBOK po prawej (na hover lub klik). Najechanie na inną kategorię przełącza
 * flyout; najechanie na pozycję-liść (Pasek danych / Zarządzaj) chowa flyout.
 *
 * Zamiast wcześniejszej nawigacji „w miejscu" (podmiana zawartości panelu bez
 * powrotu) — user nie gubi kontekstu i nie musi szukać „wstecz".
 */
function CfSubmenu({ onPreset, onManageRules }: CfSubmenuProps): React.ReactElement {
  // Która kategoria ma otwarty flyout obok (null = żadna).
  const [openSub, setOpenSub] = useState<string | null>(null);

  return (
    <div
      role="menu"
      data-testid="cf-submenu"
      className="absolute left-full top-0 ml-1 min-w-[260px] py-1 bg-white border border-gray-200 rounded-lg shadow-lg dark:bg-gray-800 dark:border-gray-700"
    >
      <CfFlyoutRow
        label="Reguły podświetlania komórek"
        Icon={Sparkles}
        isOpen={openSub === 'highlight'}
        onOpen={() => setOpenSub('highlight')}
      >
        <SubmenuRow
          label="Większe niż…"
          Icon={ArrowUp}
          onClick={() => onPreset({ type: 'highlight_cells', condition: { operator: 'gt' } })}
        />
        <SubmenuRow
          label="Mniejsze niż…"
          Icon={ArrowDown}
          onClick={() => onPreset({ type: 'highlight_cells', condition: { operator: 'lt' } })}
        />
        <SubmenuRow
          label="Pomiędzy…"
          Icon={MoveHorizontal}
          onClick={() => onPreset({ type: 'highlight_cells', condition: { operator: 'between' } })}
        />
        <SubmenuRow
          label="Równe…"
          Icon={Equal}
          onClick={() => onPreset({ type: 'highlight_cells', condition: { operator: 'eq' } })}
        />
        <SubmenuRow
          label="Tekst zawiera…"
          Icon={Type}
          onClick={() => onPreset({ type: 'highlight_cells', condition: { operator: 'contains' } })}
        />
        <SubmenuRow
          label="Warunek niestandardowy…"
          Icon={Sparkles}
          onClick={() => onPreset({ type: 'highlight_cells' })}
        />
      </CfFlyoutRow>

      <CfFlyoutRow
        label="Unikalne / Duplikaty"
        Icon={Copy}
        isOpen={openSub === 'unique_dup'}
        onOpen={() => setOpenSub('unique_dup')}
      >
        <SubmenuRow
          label="Unikalne (1 wystąpienie)"
          Icon={Star}
          onClick={() => onPreset({ type: 'unique_duplicate', condition: { mode: 'unique' } })}
        />
        <SubmenuRow
          label="Duplikaty (≥2 wystąpień)"
          Icon={Copy}
          onClick={() => onPreset({ type: 'unique_duplicate', condition: { mode: 'duplicate' } })}
        />
      </CfFlyoutRow>

      <CfFlyoutRow
        label="Skala kolorów"
        Icon={Palette}
        isOpen={openSub === 'color_scale'}
        onOpen={() => setOpenSub('color_scale')}
      >
        <SubmenuRow
          label="2-kolorowy (zielony → czerwony)"
          Icon={Palette}
          onClick={() =>
            onPreset({
              type: 'all_cells',
              condition: { subType: '2color', colorMin: '#22c55e', colorMax: '#ef4444' },
            })
          }
        />
        <SubmenuRow
          label="3-kolorowy (zielony → żółty → czerwony)"
          Icon={Palette}
          onClick={() =>
            onPreset({
              type: 'all_cells',
              condition: {
                subType: '3color',
                colorMin: '#22c55e',
                colorMid: '#fef3c7',
                colorMax: '#ef4444',
              },
            })
          }
        />
      </CfFlyoutRow>

      <SubmenuRow
        label="Pasek danych"
        Icon={BarChart3}
        onMouseEnter={() => setOpenSub(null)}
        onClick={() =>
          onPreset({
            type: 'all_cells',
            condition: { subType: 'data_bar', color: '#3b82f6', showValue: true },
          })
        }
      />

      <CfFlyoutRow
        label="Zestaw ikon"
        Icon={Star}
        isOpen={openSub === 'icon_set'}
        onOpen={() => setOpenSub('icon_set')}
      >
        <SubmenuRow
          label="Strzałki (3 stany)"
          Icon={Star}
          onClick={() =>
            onPreset({ type: 'all_cells', condition: { subType: 'icon_set', setName: 'arrows_3' } })
          }
        />
        <SubmenuRow
          label="Strzałki (5 stanów)"
          Icon={Star}
          onClick={() =>
            onPreset({ type: 'all_cells', condition: { subType: 'icon_set', setName: 'arrows_5' } })
          }
        />
        <SubmenuRow
          label="Sygnalizacja (3 stany)"
          Icon={Star}
          onClick={() =>
            onPreset({
              type: 'all_cells',
              condition: { subType: 'icon_set', setName: 'traffic_3' },
            })
          }
        />
        <SubmenuRow
          label="Gwiazdki (5 stanów)"
          Icon={Star}
          onClick={() =>
            onPreset({ type: 'all_cells', condition: { subType: 'icon_set', setName: 'stars_5' } })
          }
        />
      </CfFlyoutRow>

      <div className="my-1 border-t border-gray-200 dark:border-gray-700" />
      <SubmenuRow
        label="Zarządzaj regułami…"
        Icon={SettingsIcon}
        onMouseEnter={() => setOpenSub(null)}
        onClick={onManageRules}
      />
    </div>
  );
}

/**
 * Rozwijalny wiersz submenu CF — pokazuje chevron i otwiera flyout z `children`
 * OBOK po prawej (na hover lub klik). Parent panel zostaje widoczny.
 */
function CfFlyoutRow({
  label,
  Icon,
  isOpen,
  onOpen,
  children,
}: {
  label: string;
  Icon: typeof Filter;
  isOpen: boolean;
  onOpen: () => void;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="relative" onMouseEnter={onOpen}>
      <SubmenuRow label={label} Icon={Icon} hasSubmenu isActive={isOpen} onClick={onOpen} />
      {isOpen && (
        <div
          role="menu"
          className="absolute left-full top-0 ml-1 min-w-[240px] py-1 bg-white border border-gray-200 rounded-lg shadow-lg dark:bg-gray-800 dark:border-gray-700"
        >
          {children}
        </div>
      )}
    </div>
  );
}

function SubmenuRow({
  label,
  Icon,
  hasSubmenu,
  isActive,
  onClick,
  onMouseEnter,
}: {
  label: string;
  Icon: typeof Filter;
  hasSubmenu?: boolean;
  isActive?: boolean;
  onClick: () => void;
  onMouseEnter?: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      className={`flex w-full items-center gap-2 px-3 py-1.5 text-sm text-left transition-colors ${
        isActive
          ? 'bg-accent-50 text-accent-700 dark:bg-accent-900/20'
          : 'text-gray-700 hover:bg-accent-50 hover:text-accent-700 dark:text-gray-200 dark:hover:bg-accent-900/20'
      }`}
    >
      <Icon size={14} className="shrink-0" aria-hidden="true" />
      <span className="flex-1">{label}</span>
      {hasSubmenu && <ChevronRight size={14} aria-hidden="true" />}
    </button>
  );
}

interface MenuRowProps {
  item: MenuItem;
  isActive?: boolean;
  onClick: () => void;
}

function MenuRow({ item, isActive, onClick }: MenuRowProps): React.ReactElement {
  const { Icon } = item;
  return (
    <button
      type="button"
      role="menuitem"
      data-action={item.action}
      data-disabled={item.disabled ? 'true' : 'false'}
      onClick={onClick}
      disabled={item.disabled}
      title={item.disabled ? 'W przygotowaniu' : undefined}
      className={`flex w-full items-center gap-2 px-3 py-1.5 text-sm text-left transition-colors ${
        item.disabled
          ? 'text-gray-400 cursor-not-allowed dark:text-gray-500'
          : isActive
            ? 'bg-accent-50 text-accent-700 dark:bg-accent-900/20'
            : 'text-gray-700 hover:bg-accent-50 hover:text-accent-700 dark:text-gray-200 dark:hover:bg-accent-900/20'
      }`}
    >
      <Icon size={14} className="shrink-0" aria-hidden="true" />
      <span className="flex-1">{item.label}</span>
      {item.hasSubmenu && <ChevronRight size={14} aria-hidden="true" />}
    </button>
  );
}
